// ============================================================================
// HrHeadcountUpload.jsx
// Upload one or both Workday extracts, review what the parser found, then
// commit. Nothing reaches either dashboard until the commit — the whole point
// of the preview step is that the exception list is read first.
//
// Both files can be dropped in together in any order; the server identifies
// each by its header rather than its name, because these arrive called
// "Details_View (2) 1.csv" and worse. Either can also be sent alone: roster
// and allocations carry independent is_current pointers, so refreshing one
// does not require re-uploading the other.
// ============================================================================

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, FileText, X, AlertTriangle, CheckCircle2, Pencil, Save, Ban } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';
import { c, t, r, card, btn } from '../theme';
import PageHeader from '../components/Shared/PageHeader';

const n0 = (v) => Number(v ?? 0).toLocaleString();

// Only the first is a fault. The rest are worth reading before committing but
// do not mean the file is wrong.
const SEVERITY = {
  block_not_100:              { label: 'Allocation does not total 100%', tone: 'danger' },
  allocated_but_absent:       { label: 'Active worker missing from the headcount file', tone: 'warning' },
  referenced_but_absent:      { label: 'Manages people but has no row', tone: 'warning' },
  stale_terminated_allocation:{ label: 'Terminated workers with open allocations', tone: 'muted' },
  resolved_from_home_cc:      { label: 'Charged to home cost center', tone: 'muted' },
};

// "121 Payroll" -> { code: '121', name: 'Payroll' }, same split the backend's
// own parseCostCenterCode uses (leading run of digits is the code).
function splitRawCostCenter(raw) {
  const m = String(raw || '').trim().match(/^(\d+)\s*(.*)$/);
  return m ? { code: m[1], name: m[2] || raw } : { code: '', name: raw };
}

export default function HrHeadcountUpload() {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [provisional, setProvisional] = useState(false);
  const [snapshotDate, setSnapshotDate] = useState('');
  const inputRef = useRef(null);
  const qc = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      return api.upload('/hr-headcount/upload', fd);
    },
    onSuccess: (data) => {
      setPreview(data);
      // The monthly drop names its period (Headcount_April26.xlsx); prefill it
      // so a six-month backfill is six confirmations, not six date pickers.
      if (data.inferred_snapshot_date) setSnapshotDate(data.inferred_snapshot_date);
    },
    onError: (err) => toast.error(err.message || 'Upload failed.'),
  });

  // Adding a missing cost center on the spot, right where the gap shows up —
  // same idea as the reconciliation banner's "add to roster"/"dismiss"
  // actions. A fresh preview is required afterward since resolution happened
  // at parse time against whatever cost centers existed then; the easiest way
  // to get one is to cancel this preview batch and re-run the same files.
  const [addingCc, setAddingCc] = useState(null);
  const [ccCode, setCcCode] = useState('');
  const [ccName, setCcName] = useState('');
  const addCostCenter = useMutation({
    mutationFn: () => api.post('/admin/overtime-cost-centers', { code: ccCode, name: ccName }),
    onSuccess: async () => {
      toast.success(`${ccCode} ${ccName} added — refreshing the preview.`);
      setAddingCc(null); setCcCode(''); setCcName('');
      qc.invalidateQueries({ queryKey: ['admin-overtime-cost-centers'] });
      if (preview?.batch_id) await api.post(`/hr-headcount/batches/${preview.batch_id}/cancel`, {}).catch(() => {});
      uploadMutation.mutate();
    },
    onError: (err) => toast.error(err.message || 'Could not add that cost center.'),
  });

  const commitMutation = useMutation({
    mutationFn: () => api.commit(`/hr-headcount/batches/${preview.batch_id}/commit`, {
      snapshot_date: snapshotDate || null,
      is_provisional: provisional,
    }),
    onSuccess: () => {
      toast.success('Committed — both dashboards now show this snapshot.');
      setPreview(null); setFiles([]); setProvisional(false); setSnapshotDate('');
      qc.invalidateQueries({ queryKey: ['hr-headcount'] });
      qc.invalidateQueries({ queryKey: ['hr-allocations'] });
      qc.invalidateQueries({ queryKey: ['hr-reconciliation'] });
    },
    onError: (err) => toast.error(err.message || 'Commit failed.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/hr-headcount/batches/${preview.batch_id}/cancel`, {}),
    onSuccess: () => { toast.success('Discarded.'); setPreview(null); setFiles([]); },
    onError: (err) => toast.error(err.message || 'Could not discard.'),
  });

  // Recent batches, and a way to fix a wrong date after committing — the date
  // picker above defaults to today, and committing before changing it is an
  // easy slip. There was previously no way back from that short of a direct
  // database edit, since "current" is decided purely by comparing
  // snapshot_date across committed batches: a wrong date does not just
  // mislabel one snapshot, it can outrank a correct one and stay "current".
  const { data: batches = [] } = useQuery({
    queryKey: ['hr-headcount-batches'],
    queryFn: () => api.get('/hr-headcount/batches'),
  });
  const [fixingId, setFixingId] = useState(null);
  const [fixDate, setFixDate] = useState('');
  const invalidateDashboards = () => {
    qc.invalidateQueries({ queryKey: ['hr-headcount-batches'] });
    qc.invalidateQueries({ queryKey: ['hr-headcount'] });
    qc.invalidateQueries({ queryKey: ['hr-allocations'] });
    qc.invalidateQueries({ queryKey: ['hr-reconciliation'] });
  };
  const correctDateMutation = useMutation({
    mutationFn: (id) => api.post(`/hr-headcount/batches/${id}/correct-date`, { snapshot_date: fixDate }),
    onSuccess: () => { toast.success('Snapshot date corrected.'); setFixingId(null); setFixDate(''); invalidateDashboards(); },
    onError: (err) => toast.error(err.message || 'Could not correct the date.'),
  });

  // Distinct from Discard above (which only ever applies to a preview that
  // never went live): voiding reverses a batch that WAS committed -- the
  // wrong file entirely, not just a wrong date. Never deletes anything; the
  // batch and its rows stay, just excluded from "current" from here on.
  const voidMutation = useMutation({
    mutationFn: (id) => api.post(`/hr-headcount/batches/${id}/void`, {}),
    onSuccess: () => { toast.success('Batch voided.'); invalidateDashboards(); },
    onError: (err) => toast.error(err.message || 'Could not void the batch.'),
  });

  const pick = (list) => {
    const next = Array.from(list).slice(0, 2);
    setFiles(next);
    setPreview(null);
  };

  const s = preview?.preview;
  const grouped = {};
  for (const e of s?.exceptions || []) {
    if (e.type === 'unmapped_cost_center') continue; // already shown in the dedicated banner above
    (grouped[e.type] ||= []).push(e);
  }

  return (
    <div>
      <PageHeader title="Upload HR Headcount" subtitle="Workday headcount and costing allocation extracts" />

      {!preview && (
        <div style={{ ...card.base, padding: 24, marginBottom: 16 }}>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}
            style={{
              border: `2px dashed ${c.borderStrong}`, borderRadius: r.lg, padding: '36px 20px',
              textAlign: 'center', cursor: 'pointer', background: c.surfaceSunk,
            }}
          >
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" multiple style={{ display: 'none' }}
                   onChange={(e) => pick(e.target.files)} />
            <UploadCloud size={30} color={c.textFaint} />
            <div style={{ ...t.body, color: c.textStrong, marginTop: 10 }}>
              Drop the CSV exports here, or click to choose
            </div>
            <div style={{ ...t.bodySm, color: c.textMuted, marginTop: 6 }}>
              Either file, or both together — CSV or Excel, order does not matter.
            </div>
          </div>

          {files.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {files.map((f) => (
                <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8, ...t.bodySm, color: c.textStrong }}>
                  <FileText size={15} color={c.textMuted} />
                  {f.name}
                  <span style={{ color: c.textFaint }}>{(f.size / 1024).toFixed(0)} KB</span>
                </div>
              ))}
              <button
                style={{ ...btn.primary, marginTop: 14 }}
                disabled={uploadMutation.isPending}
                onClick={() => uploadMutation.mutate()}
              >
                {uploadMutation.isPending ? 'Reading…' : 'Read and preview'}
              </button>
              <button style={{ ...btn.secondary, marginTop: 14, marginLeft: 8 }} onClick={() => setFiles([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {preview && (
        <>
          <div style={{ ...card.base, padding: 20, marginBottom: 16 }}>
            <div style={{ ...t.h3, color: c.text, marginBottom: 4 }}>Nothing has been saved yet</div>
            <div style={{ ...t.bodySm, color: c.textMuted, marginBottom: 16 }}>
              Detected: {preview.detected.join(' and ')}. Review below, then commit or discard.
            </div>

            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 18 }}>
              {s.roster.rows > 0 && (
                <Stat label="Heads" value={n0(s.roster.heads)}
                      sub={`${n0(s.roster.rows)} rows · ${n0(s.roster.terminated_excluded)} terminated excluded · ${n0(s.roster.on_leave_counted)} on leave counted`} />
              )}
              {s.allocations.lines > 0 && (
                <Stat label="Allocation lines" value={n0(s.allocations.lines)}
                      sub={`${n0(s.allocations.blocks)} blocks · ${n0(s.allocations.workers)} workers`} />
              )}
              <Stat label="Cost centers" value={n0(s.roster.cost_centers || 0)} />
            </div>

            {s.unmapped_cost_centers.length > 0 && (
              <Callout tone="danger" icon={AlertTriangle}>
                <div>
                  {s.unmapped_cost_centers.length} cost center(s) in the file do not exist in the portal.
                  Those rows will not appear on either dashboard until the cost center is added.
                </div>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {s.unmapped_cost_centers.map((u) => (
                    <li key={u.raw_cost_center} style={{ marginBottom: 4 }}>
                      {u.raw_cost_center} ({u.row_count} row{u.row_count === 1 ? '' : 's'})
                      {addingCc !== u.raw_cost_center && (
                        <button
                          onClick={() => {
                            const { code, name } = splitRawCostCenter(u.raw_cost_center);
                            setAddingCc(u.raw_cost_center); setCcCode(code); setCcName(name);
                          }}
                          style={{ marginLeft: 10, ...t.micro, color: c.info, background: 'none', border: 'none',
                                   padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                        >add cost center</button>
                      )}
                      {addingCc === u.raw_cost_center && (
                        <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            placeholder="Code" value={ccCode} onChange={(e) => setCcCode(e.target.value)}
                            style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm, width: 70 }}
                          />
                          <input
                            placeholder="Name" value={ccName} onChange={(e) => setCcName(e.target.value)}
                            style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm, width: 220 }}
                          />
                          <button
                            disabled={!ccCode || !ccName || addCostCenter.isPending}
                            onClick={() => addCostCenter.mutate()}
                            style={{ ...t.bodySm, color: c.info, background: 'none', border: `1px solid ${c.border}`,
                                     borderRadius: r.md, padding: '4px 8px', cursor: 'pointer' }}
                          >Save</button>
                          <button
                            onClick={() => setAddingCc(null)}
                            style={{ ...t.bodySm, color: c.textMuted, background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer' }}
                          >Cancel</button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}

            {s.warnings.length > 0 && (
              <Callout tone="warning" icon={AlertTriangle}>
                {s.warnings.length} row(s) were skipped: {s.warnings.slice(0, 3).join(' · ')}
                {s.warnings.length > 3 && ` · and ${s.warnings.length - 3} more`}
              </Callout>
            )}

            {s.exception_count === 0 && (
              <Callout tone="success" icon={CheckCircle2}>Nothing unusual found.</Callout>
            )}

            {Object.entries(grouped).map(([type, list]) => {
              const meta = SEVERITY[type] || { label: type, tone: 'muted' };
              return (
                <div key={type} style={{ marginTop: 14 }}>
                  <div style={{ ...t.label, color: c.textStrong, marginBottom: 6 }}>
                    {meta.label} <span style={{ color: c.textFaint }}>({list.length})</span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {list.slice(0, 8).map((e, i) => (
                      <li key={i} style={{ ...t.bodySm, color: meta.tone === 'muted' ? c.textMuted : c.textStrong }}>
                        {e.detail}
                      </li>
                    ))}
                    {list.length > 8 && (
                      <li style={{ ...t.bodySm, color: c.textFaint }}>and {list.length - 8} more</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>

          <div style={{ ...card.base, padding: 20 }}>
            {/* Neither export carries a date of its own, so it is stated here
                rather than guessed. */}
            <div style={{ ...t.label, color: c.textStrong, marginBottom: 8 }}>Snapshot date</div>
            <input
              type="date" value={snapshotDate} onChange={(e) => setSnapshotDate(e.target.value)}
              style={{ padding: '7px 10px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm }}
            />
            <div style={{ ...t.bodySm, color: c.textMuted, marginTop: 6 }}>
              {preview.inferred_snapshot_date
                ? 'Read from the filename. Change it if the file covers a different month.'
                : 'Leave blank to use today. Set it when the export was pulled for an earlier date.'}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, ...t.bodySm, color: c.textStrong }}>
              <input type="checkbox" checked={provisional} onChange={() => setProvisional((v) => !v)} />
              Mark provisional — the extract is known to be incomplete
            </label>
            <div style={{ ...t.bodySm, color: c.textMuted, marginTop: 4, marginLeft: 24 }}>
              Shows a badge on both dashboards so the numbers are not read as final.
            </div>

            <div style={{ marginTop: 20 }}>
              <button style={btn.primary} disabled={commitMutation.isPending}
                      onClick={() => commitMutation.mutate()}>
                {commitMutation.isPending ? 'Committing…' : 'Commit'}
              </button>
              <button style={{ ...btn.secondary, marginLeft: 8 }} disabled={cancelMutation.isPending}
                      onClick={() => cancelMutation.mutate()}>
                <X size={14} style={{ marginRight: 4 }} /> Discard
              </button>
            </div>
          </div>
        </>
      )}

      {batches.length > 0 && (
        <div style={{ ...card.base, padding: 20, marginTop: 16 }}>
          <div style={{ ...t.h3, color: c.text, marginBottom: 4 }}>Recent batches</div>
          <div style={{ ...t.bodySm, color: c.textMuted, marginBottom: 14 }}>
            "Current" is decided by snapshot date, not upload time — fix a wrong one here
            rather than re-uploading, since a later wrong date would still outrank a
            correct re-upload.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                {['File', 'Status', 'Snapshot date', 'Current', ''].map((h) => (
                  <th key={h} style={{ ...t.label, color: c.textMuted, textAlign: 'left', padding: '6px 8px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} style={{ borderBottom: `1px solid ${c.borderLight || c.border}` }}>
                  <td style={{ ...t.bodySm, color: c.textStrong, padding: '8px' }}>
                    {b.roster_file_name || b.allocation_file_name}
                    {b.is_month_end && (
                      <span
                        title="Month-end: the authoritative final number for the month, not just a weekly snapshot"
                        style={{
                          marginLeft: 8, fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                          letterSpacing: 0.4, color: c.successText, background: c.successTint,
                          borderRadius: 10, padding: '2px 7px',
                        }}
                      >Final</span>
                    )}
                  </td>
                  <td style={{ ...t.bodySm, color: c.textMuted, padding: '8px' }}>{b.status}</td>
                  <td style={{ ...t.bodySm, color: c.textStrong, padding: '8px', fontVariantNumeric: 'tabular-nums' }}>
                    {fixingId === b.id ? (
                      <input
                        type="date" value={fixDate} onChange={(e) => setFixDate(e.target.value)}
                        style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm }}
                      />
                    ) : (b.snapshot_date || '—')}
                  </td>
                  <td style={{ ...t.bodySm, color: c.textMuted, padding: '8px' }}>
                    {[b.is_current_roster && 'headcount', b.is_current_allocations && 'allocations'].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {b.status === 'committed' && (
                      fixingId === b.id ? (
                        <>
                          <button
                            style={btn.icon} title="Save"
                            disabled={correctDateMutation.isPending || !fixDate}
                            onClick={() => correctDateMutation.mutate(b.id)}
                          ><Save size={14} /></button>
                          <button style={btn.icon} title="Cancel" onClick={() => { setFixingId(null); setFixDate(''); }}>
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            style={btn.icon} title="Fix date"
                            onClick={() => { setFixingId(b.id); setFixDate(b.snapshot_date || ''); }}
                          ><Pencil size={14} /></button>
                          <button
                            style={btn.icon} title="Void — this was the wrong file, not just a wrong date"
                            disabled={voidMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Void "${b.roster_file_name || b.allocation_file_name}"? It stops counting as current. Nothing is deleted.`)) {
                                voidMutation.mutate(b.id);
                              }
                            }}
                          ><Ban size={14} /></button>
                        </>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div style={{ ...t.label, color: c.textMuted }}>{label}</div>
      <div style={{ ...t.h2, color: c.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ ...t.bodySm, color: c.textFaint }}>{sub}</div>}
    </div>
  );
}

function Callout({ tone, icon: Icon, children }) {
  const bg = { danger: c.dangerTint, warning: c.warningTint, success: c.successTint, muted: c.canvas }[tone];
  const fg = { danger: c.dangerText, warning: c.warningText, success: c.successText, muted: c.textMuted }[tone];
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: bg, color: fg,
                  padding: '10px 14px', borderRadius: r.md, marginTop: 12, ...t.bodySm }}>
      <Icon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>{children}</div>
    </div>
  );
}
